import { ensureEarthEngineReady } from '../../services/earth-engine';
import { Actions as Mepbay } from '../../store/ducks/mepbay';
import { aoiGeometry } from '../../common/aoi';

export const SESSION_EXPORT_VERSION = 1;
export const SESSION_EXPORT_MODULE = 'mepbay';

const PENDING_MEPBAY_STORAGE_KEY = 'mepbaySessionImportLayers';

/**
 * @function buildSessionExport
 * @description Bundles everything needed to redraw a completed MEPBay parabolic-bay analysis
 * without re-clicking through the wizard. Unlike CSqueeze/Bathymetry, MEPBay's imagery is just a
 * visual backdrop (the parabolic model depends only on the three control points per layer, not on
 * any specific satellite image) and is never composited — so re-acquiring it is just a normal
 * `Mepbay.acquireImage` dispatch per previously-acquired image, not a hand-rolled Earth Engine
 * rebuild. The parabolic model itself (`calculateWithTurf`) is pure client-side math, so only the
 * three control points per layer need to be exported — `importSessionState` leaves the actual
 * recompute/redraw (markers, lines, parabola) to ProcessingPage's own mount effect.
 * @param {Object} params
 * @param {Array<Array<Number>>} params.coordinates - AOI coordinates, `state.mepbay.coordinates`.
 * @param {Number} params.satelliteIndex - `state.mepbay.satelliteIndex`.
 * @param {Array<Object>} params.availableDates - `state.mepbay.availableDates` — the exact images
 * the user selected/finished with in PeriodImageChooser (step 3). Not derivable from `images`
 * (acquired imagery is a subset the user chose to actually load) — without this, ImageChooserCard
 * has nothing to list on the result page (it renders nothing at all while this is undefined), so
 * there'd be no way to load further images after importing.
 * @param {Object} params.images - `state.imagery.images`, keyed by numeric image id.
 * @param {Object} params.layers - `ProcessingPage`'s own `layers` local state, keyed by layer id.
 * **Called by:
 * @see {ProcessingPage} - MepBay's result page, via the "export session for testing" icon.
 * @memberof module:Mepbay
 * @returns {Object} A plain, JSON-serializable session snapshot.
 */
export const buildSessionExport = ({ coordinates, satelliteIndex, availableDates, images, layers }) => {
  const imageList = Object.values(images || {}).map((image) => ({
    missionName: image.missionName,
    date: image.date,
  }));

  const layerList = Object.values(layers || {})
    .filter((layer) => !layer.isROI)
    .map((layer) => ({
      id: layer.id,
      name: layer.name,
      color: layer.color,
      opacity: layer.opacity,
      visible: layer.visible,
      controlPoints: layer.controlPoints,
    }));

  return {
    version: SESSION_EXPORT_VERSION,
    module: SESSION_EXPORT_MODULE,
    exportedAt: new Date().toISOString(),
    satelliteIndex,
    coordinates,
    availableDates: availableDates || [],
    images: imageList,
    layers: layerList,
  };
};

/**
 * @function importSessionState
 * @description Restores the satellite/AOI selection and re-dispatches an `acquireImage` action per
 * previously-acquired image (the saga re-runs the real Earth Engine acquisition, exactly like
 * clicking each image in ImageChooserCard would). Layer control points can't be dispatched to Redux
 * (they're ProcessingPage's own local state) so they're stashed for its own mount effect instead.
 * @param {Function} dispatch - Redux dispatch.
 * @param {Object} exported - A session snapshot produced by `buildSessionExport`.
 * @see {@link buildSessionExport}
 * **Called by:
 * @see {NavigationBar} - via the standardized "import session for testing" icon's onImport.
 * @memberof module:Mepbay
 * @returns {void}
 */
export const importSessionState = async (dispatch, exported) => {
  await ensureEarthEngineReady();
  const { satelliteIndex, coordinates, availableDates, images, layers } = exported;

  dispatch(Mepbay.setSatellite(satelliteIndex));

  const geometry = aoiGeometry(coordinates);
  dispatch(Mepbay.setAOI(null, coordinates, geometry));

  dispatch(Mepbay.setAvailableDates(availableDates || []));

  (images || []).forEach(({ missionName, date }) => {
    dispatch(Mepbay.acquireImage(missionName, date));
  });

  if (layers && layers.length) {
    window.sessionStorage.setItem(PENDING_MEPBAY_STORAGE_KEY, JSON.stringify(layers));
  } else {
    window.sessionStorage.removeItem(PENDING_MEPBAY_STORAGE_KEY);
  }
};

/**
 * @function readPendingMepBaySession
 * @description Reads and clears the layer data `importSessionState` left waiting (see
 * `PENDING_MEPBAY_STORAGE_KEY`'s comment), if any. A no-op read (returns `null`) if nothing is
 * pending, so it's safe to call unconditionally.
 * @see {@link importSessionState}
 * **Called by:
 * @see {ProcessingPage} - MepBay's result page, once its map has mounted.
 * @memberof module:Mepbay
 * @returns {Array|null} The exported `layers` array, or `null`.
 */
export const readPendingMepBaySession = () => {
  const raw = window.sessionStorage.getItem(PENDING_MEPBAY_STORAGE_KEY);
  if (!raw) return null;
  window.sessionStorage.removeItem(PENDING_MEPBAY_STORAGE_KEY);
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
};
